import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Typography,
  Box,
  Divider
} from '@material-ui/core';
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  Diamond as DiamondIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import useSettings from '../../hooks/useSettings';
import EfiSettings from '../PaymentGateways/Efi/EfiSettings';
import OwenSettings from '../PaymentGateways/Owen/OwenSettings';
import OwenAd from '../PaymentGateways/Owen/OwenAd';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    '& svg': {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },
  sectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  gatewayOption: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    '& svg': {
      marginRight: theme.spacing(1.5),
      fontSize: '1.25rem',
    },
  },
  gatewayName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  gatewayLabel: {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  gatewayDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  badge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  contentSection: {
    marginTop: theme.spacing(3),
  },
}));

export default function PaymentGateway({ settings }) {
  const classes = useStyles();
  const [paymentGateway, setPaymentGateway] = useState('');
  const { update } = useSettings();

  useEffect(() => {
    if (Array.isArray(settings) && settings.length) {
      const paymentGatewaySetting = settings.find(
        (s) => s.key === '_paymentGateway'
      );
      if (paymentGatewaySetting) {
        setPaymentGateway(paymentGatewaySetting.value);
      }
    }
  }, [settings]);

  async function handleChangePaymentGateway(value) {
    try {
      setPaymentGateway(value);
      await update({
        key: '_paymentGateway',
        value,
      });
      toast.success('Gateway de pagamento atualizado com sucesso');
    } catch (err) {
      toast.error('Erro ao atualizar gateway de pagamento');
    }
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <PaymentIcon />
          <Typography className={classes.sectionTitle}>
            Gateway de Pagamento
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="payment-gateway-label">
                Selecione o Gateway
              </InputLabel>
              <Select
                labelId="payment-gateway-label"
                value={paymentGateway}
                onChange={(e) => handleChangePaymentGateway(e.target.value)}
                label="Selecione o Gateway"
              >
                <MenuItem value="">
                  <Box className={classes.gatewayOption}>
                    <CreditCardIcon />
                    <Box>
                      <Box className={classes.gatewayName}>
                        <Typography className={classes.gatewayLabel}>
                          Nenhum
                        </Typography>
                      </Box>
                      <Typography className={classes.gatewayDescription}>
                        Desabilitar integrações de pagamento
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>

                <MenuItem value="owen">
                  <Box className={classes.gatewayOption}>
                    <DiamondIcon sx={{ color: '#3b82f6' }} />
                    <Box>
                      <Box className={classes.gatewayName}>
                        <Typography className={classes.gatewayLabel}>
                          Owen Payments
                        </Typography>
                        <span className={classes.badge}>Recomendado</span>
                      </Box>
                      <Typography className={classes.gatewayDescription}>
                        Solução completa de pagamentos
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>

                <MenuItem value="efi">
                  <Box className={classes.gatewayOption}>
                    <CreditCardIcon sx={{ color: '#10b981' }} />
                    <Box>
                      <Box className={classes.gatewayName}>
                        <Typography className={classes.gatewayLabel}>
                          Efí
                        </Typography>
                      </Box>
                      <Typography className={classes.gatewayDescription}>
                        Gateway de pagamentos alternativo
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {paymentGateway && <Divider className={classes.divider} />}

      <Box className={classes.contentSection}>
        {paymentGateway === 'efi' && <EfiSettings settings={settings} />}
        {paymentGateway === 'owen' && <OwenSettings settings={settings} />}
        {paymentGateway === '' && <OwenAd />}
      </Box>
    </Paper>
  );
}